import { en } from 'chrono-node';
import { localDateTime } from './api';
import { extractSpokenClocks, resolveSpokenClock } from './spokenClock';
import { dayKey } from './calendarDates';

const uncertain = (reason, date = null) => ({ value: null, date, uncertain: true, enforce: true, reason });
const removeSpans = (text, spans) => [...spans].sort((a, b) => b.index - a.index).reduce((value, span) => `${value.slice(0, span.index)} ${value.slice(span.end)}`, text);
const knownCalendarDay = (text, spans, reference) => {
  const dayReference = new Date(reference); dayReference.setHours(0, 0, 0, 0);
  const matches = en.casual.parse(removeSpans(text, spans), dayReference, { forwardDate: true });
  if (matches.length !== 1 || matches[0].end) return null;
  const date = matches[0].start;
  return ['day', 'month', 'weekday', 'year'].some(part => date.isCertain(part)) ? dayKey(date.date()) : null;
};

export function captureSchedule(text, reference = new Date()) {
  const source = text.normalize('NFKC').replace(/[’‘]/g, "'");
  const { clocks, ignored, unsupported, compactConflict } = extractSpokenClocks(source);
  if (clocks.length > 1 || compactConflict) return uncertain('multiple-times', knownCalendarDay(source, [...clocks, ...ignored], reference));
  if (unsupported) return uncertain('unrecognized-time', knownCalendarDay(source, ignored, reference));
  if (!clocks.length) {
    // A date is not an implicit9AM appointment. Store an untimed ISO day in
    // the existing detected_date field; scheduled_at stays null.
    const period = source.toLowerCase().match(/\b(morning|afternoon|evening|tonight|night)\b/)?.[1];
    const dateReference = new Date(reference);
    if (!/\b(?:in|after)\s+(?:[\w -]+|\d+(?:\.\d+)?)\s+(?:hours?|minutes?|seconds?)\b/i.test(source)) dateReference.setHours(0, 0, 0, 0);
    const matches = en.casual.parse(removeSpans(source, ignored), dateReference, { forwardDate: true });
    if (!matches.length) return { value: null, date: null, uncertain: /\b(?:sometime|someday|later|soon|next week|next month)\b/i.test(source), enforce: !!ignored.length };
    if (matches.length > 1 || matches[0].end) return uncertain('multiple-dates');
    const date = matches[0].start.date();
    const start = matches[0].start;
    if (!start.isCertain('hour') && !period) return { value: null, date: dayKey(date), allDay: true, uncertain: false, enforce: true, reason: 'date-only' };
    if (period && !start.isCertain('hour')) {
      date.setHours(({ morning: 9, afternoon: 15, evening: 19, tonight: 19, night: 21 })[period], 0, 0, 0);
      if (start.isCertain('weekday') && !start.isCertain('day') && date <= reference) date.setDate(date.getDate() + 7);
      else if (!start.isCertain('day') && !start.isCertain('weekday') && !/\b(?:today|tonight|this)\b/i.test(source) && date <= reference) date.setDate(date.getDate() + 1);
    }
    return { value: localDateTime(date), date: null, uncertain: false, enforce: true, reason: period ? 'general-time' : 'relative-time' };
  }
  const clock = clocks[0];
  const resolved = resolveSpokenClock(clock, source);
  if (!resolved) {
    const known = knownCalendarDay(source, [...clocks, ...ignored], reference);
    return { ...uncertain('ambiguous-meridiem', known), ambiguousMeridiem: true };
  }
  let dateText = removeSpans(source, [...clocks, ...ignored]);
  if (/\b(?:sometime|someday|maybe|soon)\b/i.test(dateText) || /\bin\s+(?:\d+|\w+)\s+(?:hours?|minutes?)\b/i.test(dateText)) return uncertain('unclear-date');
  dateText = dateText.replace(/\bthis\s+(?:morning|afternoon|evening|night)\b/gi, 'today')
    .replace(/\btonight\b/gi, 'today')
    .replace(/\b(?:(?:in\s+the|in|at)\s+)?(?:morning|afternoon|evening|night)\b/gi, ' ');
  // Parse calendar dates at local midnight, then apply the resolved clock.
  // This avoids chrono's implied noon rolling a same-day evening into next week.
  const dayReference = new Date(reference); dayReference.setHours(0, 0, 0, 0);
  const dates = en.casual.parse(dateText, dayReference, { forwardDate: true });
  if (dates.length > 1 || dates[0]?.end) return uncertain('multiple-dates');
  const parsed = dates[0]?.start;
  const date = parsed ? parsed.date() : new Date(reference);
  date.setHours(resolved.hour, resolved.minute, 0, 0);
  if (resolved.dayShift) date.setDate(date.getDate() + resolved.dayShift);
  if (/\btonight\b/i.test(source) && resolved.baseHour < 6) date.setDate(date.getDate() + 1);
  if (!parsed) {
    while (date <= reference) date.setDate(date.getDate() + 1);
  } else if (parsed.isCertain('weekday') && !parsed.isCertain('day') && date <= reference) {
    date.setDate(date.getDate() + 7);
  }
  // Never silently shift an explicitly spoken clock through a DST gap.
  if (date.getHours() !== resolved.hour || date.getMinutes() !== resolved.minute) return uncertain('unavailable-local-time');
  return { value: localDateTime(date), date: null, uncertain: false, enforce: true, reason: clock.meridiem || clock.absolute ? 'explicit-time' : 'contextual-time' };
}